'use client';

import {HtmlContent} from 'common/components/html_content';
import {LevelPillTag} from 'iq-questions/components/level_pilltag';
import {TopicPillTag} from 'iq-questions/components/topic_pilltag';
import type {Question} from 'iq-questions/types';
import {track} from 'iq-tracking';
import {FRONTEND_URL} from 'config';
import {ChevronDown, Lock} from 'lucide-react';
import {Fragment, useState} from 'react';
import UnlockInterviewGuide from 'iq-upselling/components/unlock_interview_guide';

// ─── Expanded row content ─────────────────────────────────────────────────────

function ExpandedRow({question}: {question: Question}) {
  if (question.is_locked) {
    return null;
  }

  if (question.body === null) {
    throw new Error('Error showing question body');
  }

  return (
    <div className='px-5 py-5 border-b border-iq-grey-light bg-gray-50'>
      <div className='relative pt-3 pb-5'>
        <HtmlContent
          html={question.body}
          className='text-sm text-iq-grey-dark leading-relaxed line-clamp-4'
        />
      </div>
      <div>
        <a
          href={`${FRONTEND_URL}/questions/${question.slug}`}
          className='inline-block px-4 py-2 bg-iq-orange text-white text-xs font-semibold rounded-md hover:opacity-90 transition-opacity'>
          View question
        </a>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

interface QuestionsTableProps {
  questions: Question[];
  onShare: () => void;
}

export function QuestionsTable({questions, onShare}: QuestionsTableProps) {
  const [openId, setOpenId] = useState<Question['id'] | null>(null);
  const [unlockOpen, setUnlockOpen] = useState(false);

  function toggle(question: Question) {
    if (question.is_locked) {
      setUnlockOpen(true);
      return;
    }
    if (openId !== question.id) {
      void track({
        event: 'question_expanded',
        action: 'expand',
        component: 'button',
        content_type: 'question',
        content_id: question.id,
      });
    }
    setOpenId((prev) => (prev === question.id ? null : question.id));
  }

  return (
    <div className='bg-white border border-iq-grey-light rounded-lg overflow-hidden'>
      <table className='w-full border-collapse'>
        <thead>
          <tr className='border-b border-iq-grey-light'>
            <th className='text-xs font-semibold text-iq-grey-medium uppercase tracking-wide text-left px-5 py-3 w-full'>
              Question
            </th>
            <th className='text-xs font-semibold text-iq-grey-medium uppercase tracking-wide text-left px-5 py-3 w-48 hidden sm:table-cell'>
              Topic
            </th>
            <th className='text-xs font-semibold text-iq-grey-medium uppercase tracking-wide text-left px-5 py-3 w-28 hidden sm:table-cell'>
              Difficulty
            </th>
            <th className='w-10' />
          </tr>
        </thead>
        <tbody>
          {questions.map((q) => {
            const isOpen = openId === q.id;
            return (
              <Fragment key={q.id}>
                <tr
                  onClick={() => toggle(q)}
                  className={`border-b border-iq-grey-light last:border-b-0 hover:bg-gray-50 transition-colors cursor-pointer ${isOpen && 'border-b-gray-100'}`}>
                  <td className='px-5 py-4'>
                    {q.is_locked ? (
                      <span className='text-sm underline text-iq-black cursor-pointer'>
                        {q.title}
                      </span>
                    ) : (
                      <a
                        href={`${FRONTEND_URL}/questions/${q.slug}`}
                        onClick={(e) => e.stopPropagation()}
                        className='text-sm underline text-iq-black hover:text-iq-orange transition-colors'>
                        {q.title}
                      </a>
                    )}
                  </td>
                  <td className='px-5 py-4 hidden sm:table-cell'>
                    <TopicPillTag name={q.topic.name} />
                  </td>
                  <td className='px-5 py-4 hidden sm:table-cell'>
                    <LevelPillTag level={q.level} />
                  </td>
                  <td className='pr-5 py-4'>
                    {q.is_locked ? (
                      <Lock size={14} className='text-iq-grey-medium' />
                    ) : (
                      <ChevronDown
                        className={`w-4 h-4 text-iq-grey-medium transition-transform duration-200 ${
                          isOpen ? 'rotate-180' : ''
                        }`}
                      />
                    )}
                  </td>
                </tr>
                {isOpen && (
                  <tr className='border-black border-t-0'>
                    <td colSpan={4}>
                      <ExpandedRow question={q} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>

      {unlockOpen && (
        <UnlockInterviewGuide
          company=''
          totalCount={0}
          onClose={() => setUnlockOpen(false)}
          onShare={() => {
            setUnlockOpen(false);
            onShare();
          }}
        />
      )}
    </div>
  );
}
